import { createSlice } from '@reduxjs/toolkit';
import {
  fetchClusterData,
  fetchImportFiles,
  exportFileWithInputs,
  exportAutoClusterFile,
  exportRAGFile,
  syncRAGFile,
  deleteRAGFile,
  fetchRAGFiles,
  exportFileToS3,
  exportAutoClusterFileFromS3,
  exportClusterFile
} from '../thunks/segmenterThunks';

const initialState = {
  clusterData: null,
  clusterOutputString: '',
  categoricalData: null,
  categoricalDataString: '',
  numericData: null,
  numericalDataString: '',
  columnData: null,
  inputFileList: ['input_data/data.csv'],
  inputDataFileName: '',
  inputFileMapping: [],
  dialogOpen: false,
  loadingImportFiles: false,
  loading: true,
  showExportFilePopUp: false,
  exportFileApiResponseMsg: null,
  exportAutoClusterFileApiResponseMsg: null,
  exportAutoClusterFileApiResponse: null,
  loadingExportFileApiResponse: false,
  loadingExportAutoClusterFileApiResponse: false,
  rawData: null,
  clusterOutputDataUrl: '',
  aggregatedCombinedData: null,
  importFileName: '',
  activeTab: 0,
  defaultXaxis: '',
  defaultYaxis: '',
  autoClusterReponse: null,
  ragFileList: [],
  loadingRAGFiles: false,
  loadingExportRAGFileResponse: false,
  loadingDeleteRAGFileResponse: false,
  loadingSyncRAGFileResponse: false,
  matchColorsToGraph: false,
  loadingPushToS3: false,
  loadingexportAutoClusterFileFromS3Response: false,
};

const segmenterSlice = createSlice({
  name: 'segmenter',
  initialState,
  reducers: {
    setImportFilesLoading(state, action) {
      state.loadingImportFiles = action.payload;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setShowExportFilePopUpModal(state, action) {
      state.showExportFilePopUp = action.payload;
    },
    setDialogOpen(state, action) {
      state.dialogOpen = action.payload;
    },
    setExportFileApiResponseLoading(state, action) {
      state.loadingExportFileApiResponse = action.payload;
    },
    setImportFileName(state, action) {
      state.importFileName = action.payload;
    },
    setExportFileApiResponseMsg(state, action) {
      state.exportFileApiResponseMsg = action.payload;
    },
    setActiveTab(state, action) {
      state.activeTab = action.payload;
    },
    setMatchColorsToGraph(state, action) {
      state.matchColorsToGraph = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClusterData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchClusterData.fulfilled, (state, action) => {
        state.clusterData = action.payload.cluster_output_json;
        state.clusterOutputString = action.payload.cluster_output_string;
        state.categoricalData = action.payload.categorical_data_json;
        state.numericData = action.payload.numeric_data_json;
        state.columnData = action.payload.columns;
        state.categoricalDataString = action.payload.categorical_data_string;
        state.numericalDataString = action.payload.numeric_data_string;
        state.rawData = action.payload.raw_data_json;
        state.aggregatedCombinedData = action.payload.aggregate_combined_data_json;
        state.defaultXaxis = action.payload.default_x_axis;
        state.defaultYaxis = action.payload.default_y_axis;
        state.clusterOutputDataUrl = action.payload.raw_data_json_url;
        state.loading = false;
      })
      .addCase(fetchClusterData.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchImportFiles.pending, (state) => {
        state.dialogOpen = true;
        state.loadingImportFiles = true;
      })
      .addCase(fetchImportFiles.fulfilled, (state, action) => {
        state.inputFileList = action.payload['input_file_list'];
        state.inputDataFileName = action.payload['input_data_file_name'];
        state.inputFileMapping = action.payload['file_list_output'];
        state.loadingImportFiles = false;
      })
      .addCase(fetchImportFiles.rejected, (state) => {
        state.loadingImportFiles = false;
      })
      .addCase(exportFileWithInputs.pending, (state) => {
        state.loadingExportFileApiResponse = true;
      })
      .addCase(exportFileWithInputs.fulfilled, (state, action) => {
        state.loadingExportFileApiResponse = false;
        state.exportFileApiResponseMsg = 'Your file has been saved successfully.';
        state.showExportFilePopUp = false;
      })
      .addCase(exportFileWithInputs.rejected, (state) => {
        state.loadingExportFileApiResponse = false;
        state.exportFileApiResponseMsg = 'Failed to export file.';
      })
      .addCase(exportAutoClusterFile.pending, (state) => {
        state.loadingExportAutoClusterFileApiResponse = true;
      })
      .addCase(exportAutoClusterFile.fulfilled, (state, action) => {
        state.loadingExportAutoClusterFileApiResponse = false;
        state.exportAutoClusterFileApiResponse = action.payload;
        state.exportAutoClusterFileApiResponseMsg = 'Successfully Uploaded';
      })
      .addCase(exportAutoClusterFile.rejected, (state, action) => {
        state.loadingExportAutoClusterFileApiResponse = false;
        state.exportAutoClusterFileApiResponse = action.payload;
        state.exportAutoClusterFileApiResponseMsg = 'File with same name already exists';
      })
      .addCase(exportAutoClusterFileFromS3.pending, (state) => {
        state.loadingexportAutoClusterFileFromS3Response = true;
      })
      .addCase(exportAutoClusterFileFromS3.fulfilled, (state, action) => {
        state.loadingexportAutoClusterFileFromS3Response = false;
      })
      .addCase(exportAutoClusterFileFromS3.rejected, (state) => {
        state.loadingexportAutoClusterFileFromS3Response = false;
      })
      .addCase(fetchRAGFiles.pending, (state) => {
        state.loadingRAGFiles = true;
      })
      .addCase(fetchRAGFiles.fulfilled, (state, action) => {
        state.ragFileList = action.payload['FileList'];
        state.loadingRAGFiles = false;
      })
      .addCase(fetchRAGFiles.rejected, (state) => {
        state.loadingRAGFiles = false;
      }) 
      .addCase(exportRAGFile.pending, (state) => {
        state.loadingExportRAGFileResponse = true;
      })
      .addCase(exportRAGFile.fulfilled, (state, action) => {
        state.loadingExportRAGFileResponse = false;
      })
      .addCase(exportRAGFile.rejected, (state) => {
        state.loadingExportRAGFileResponse = false;
      }) 
      .addCase(deleteRAGFile.pending, (state) => {
        state.loadingDeleteRAGFileResponse = true;
      })
      .addCase(deleteRAGFile.fulfilled, (state, action) => {
        state.loadingDeleteRAGFileResponse = false;
      })
      .addCase(deleteRAGFile.rejected, (state) => {
        state.loadingDeleteRAGFileResponse = false;
      }) 
      .addCase(syncRAGFile.pending, (state) => {
        state.loadingSyncRAGFileResponse = true;
      })
      .addCase(syncRAGFile.fulfilled, (state, action) => {
        state.loadingSyncRAGFileResponse = false;
      })
      .addCase(syncRAGFile.rejected, (state) => {
        state.loadingSyncRAGFileResponse = false;
      }) 
      .addCase(exportFileToS3.pending, (state) => {
        state.loadingPushToS3 = true;
      })
      .addCase(exportFileToS3.fulfilled, (state, action) => {
        state.loadingPushToS3 = false;
      })
      .addCase(exportFileToS3.rejected, (state) => {
        state.loadingPushToS3 = false;
      }) 
      .addCase(exportClusterFile.pending, (state) => {
        state.loadingExportFileApiResponse = true;
      })
      .addCase(exportClusterFile.fulfilled, (state, action) => {
        state.loadingExportFileApiResponse = false;
        state.exportFileApiResponseMsg = 'Your file has been saved successfully.';
      })
      .addCase(exportClusterFile.rejected, (state) => {
        state.loadingExportFileApiResponse = false;
        state.exportFileApiResponseMsg = 'Failed to export file.';
      })    
  },
});

export const {
  setImportFilesLoading,
  setLoading,
  setShowExportFilePopUpModal,
  setDialogOpen,
  setExportFileApiResponseLoading,
  setImportFileName,
  setExportFileApiResponseMsg,
  setActiveTab,
  setMatchColorsToGraph
} = segmenterSlice.actions;
export default segmenterSlice.reducer; 